import { CONSOLE_PRODUCT, type Permission } from '@ctcells/core'
import { t } from '@ctcells/i18n'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router'

import { ThemeToggle } from '../theme/ThemeToggle.tsx'

import '../atoms/atoms.css' // .a-sr(시각적 숨김) — 이 파일은 원자를 쓰지 않아 직접 건다

import { BatchStripContainer } from './BatchStripContainer.tsx'
import type { BatchStripProps } from './batchStripTypes.ts'
import { BrandLogo } from './navIcons.tsx'
import { useShellState } from './shellState.ts'
import { stripVariantFor } from './stripVariant.ts'
import { SurfaceContext } from './surfaceContext.ts'
import { TABLET_NAV, TABLET_PATH } from './tabletNav.ts'

import './shell.css'
import './tablet.css'

export interface TabletLayoutProps {
  /** 작업 줄 **덮어쓰기**. 값은 `BatchStripContainer` 가 서버에서 받아 채운다(시험·스토리용) */
  strip?: Partial<BatchStripProps>
  /**
   * 서버가 준 보유 권한. `undefined` = 아직 모름 → 잠금 표시를 하지 않는다.
   * 🔴 프론트가 역할에서 권한을 유도하지 않는다. 집행은 서버 403 INSUFFICIENT_ROLE 이다.
   */
  permissions?: readonly Permission[] | undefined
}

/**
 * 태블릿 kiosk 셸 — **콘솔 축약형**. 사이드바 대신 머리줄 알약 줄 하나이고 작업 줄은 콘솔과 같다.
 *
 * 콘솔 앱 안의 한 모드다(별도 배포 단위가 아니다). 싣는 화면은 `tabletNav.ts` 가 쥔다 —
 * 3종(모니터링 · 작업 · 알람)이고 고른 근거는 그 파일과 `docs/screens-pad.md` 에 있다.
 *
 * 🔴 **권한 없는 항목을 숨기지 않는다.** 자물쇠 + 비활성 + 사유다(§5-6). 숨김은 보안이 아니다.
 * 🔴 **12인치 고정 레이아웃이다.** 여기에 해상도 숫자가 없고 미디어 쿼리도 없다 —
 *    뷰포트 맞춤은 `designViewport.ts` 한 곳이 쥔다.
 */
export function TabletLayout({ strip, permissions }: TabletLayoutProps = {}) {
  const shell = useShellState()
  const navigate = useNavigate()
  const batchesPath = TABLET_PATH['batches']
  const monitoringPath = TABLET_PATH['monitoring']
  /* 🔴 작업 줄 2행의 모습은 경로가 정한다 — 태블릿 모니터링은 공정 칸 셋, 작업 · 알람은 현재 공정 한 줄(`stripVariant.ts`) */
  const stripVariant = stripVariantFor(useLocation().pathname, monitoringPath)

  const locked = (permission: Permission): boolean =>
    permissions !== undefined && !permissions.includes(permission)

  return (
    <SurfaceContext value="tablet">
      {/* 🔴 표면 선언 — 확약 창구(`confirm_source`)가 여기서 정해진다(`surfaceContext.ts`) */}
      <div className="shell shell--header">
        <div className="shell__cols">
          <main className={`shell__main${shell.eventsOpen ? ' shell__main--events' : ''}`}>
            <header className="padhead">
              <span className="padhead__brand">
                <BrandLogo />
                <span className="padhead__tag">{CONSOLE_PRODUCT}</span>
              </span>

              <nav aria-label={t('nav.aria')} className="tabnav">
                {TABLET_NAV.map((item) => {
                  const isLocked = locked(item.permission)
                  return (
                    <NavLink
                      aria-disabled={isLocked || undefined}
                      className="tabnav__item"
                      key={item.id}
                      onClick={(event) => {
                        if (isLocked) event.preventDefault()
                      }}
                      title={
                        isLocked ? t('reason.noPermission', { perm: item.permission }) : undefined
                      }
                      to={item.path}
                    >
                      {t(item.labelKey)}
                      {isLocked ? (
                        <>
                          <svg aria-hidden="true" className="tabnav__lock" viewBox="0 0 24 24">
                            <rect
                              fill="currentColor"
                              height="10"
                              rx="1.5"
                              width="14"
                              x="5"
                              y="10"
                            />
                            <path
                              d="M8 10V7a4 4 0 0 1 8 0v3"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.4"
                            />
                          </svg>
                          <span className="a-sr">
                            {t('reason.noPermission', { perm: item.permission })}
                          </span>
                        </>
                      ) : null}
                    </NavLink>
                  )
                })}
              </nav>

              <div className="padhead__auth">
                <ThemeToggle />
              </div>
            </header>

            {/*
              🔴 **태블릿 경로로 간다 — `PATHS` 를 쓰면 kiosk 밖으로 튕긴다.**
              🔴 **`onOpenRecipe` 를 넘기지 않는다.** 태블릿에 레시피 상세 라우트가 없다
                 (`tabletRoutes.tsx` — 모니터링·작업·알람 3종뿐). 갈 곳이 없으면 이동을 만들지
                 않고, 작업 줄이 그 자리를 **글자로** 그린다. 없는 곳으로 보내면 404 다.
            */}
            <BatchStripContainer
              eventsOpen={shell.eventsOpen}
              onToggleEvents={shell.toggleEvents}
              variant={stripVariant}
              {...(batchesPath === undefined
                ? {}
                : {
                    onOpenBatch: (batchId: string) => void navigate(`${batchesPath}/${batchId}`),
                    onSelectBatch: () => void navigate(batchesPath),
                  })}
              {...(monitoringPath === undefined
                ? {}
                : { onOpenMonitoring: () => void navigate(monitoringPath) })}
              {...(strip === undefined ? {} : { strip })}
            />

            {shell.eventsOpen ? null : (
              <div className="shell__body shell__body--pad">
                <Outlet />
              </div>
            )}
          </main>
        </div>
      </div>
    </SurfaceContext>
  )
}
